/**
 * Explains the non-working-hours wash on the shipped scheduling calendar.
 *
 * The grey already told the recruiter "not this time"; this says why. The maths
 * behind the wash is untouched — this only reads what is already on screen.
 *
 * A hook taking the caller's OWN root ref, rather than a wrapper component like
 * the Schedule-All wizard's: this calendar's root already carries
 * `useAllDayBlockedHoverTooltip`'s ref, and a second wrapper would push another
 * `position: relative` box into a shipped flex layout. Two listeners on one
 * element cost nothing; a re-indented JSX tree risks a layout regression.
 *
 * Detection is `elementsFromPoint`, not a per-node listener, because the wash is
 * a full-height background region that events paint on top of — a listener on
 * the wash itself goes quiet the moment a block is in the way. Same mechanism as
 * the sibling `useAllDayBlockedHoverTooltip` and `MinimumNoticeHoverPopover`.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  UnavailableHoursHoverCard,
  UNAVAILABLE_HOURS_CARD_HEIGHT_PX,
  UNAVAILABLE_HOURS_CARD_WIDTH_PX,
} from './UnavailableHoursHoverCard';
import { pickAllDayTitlesFromStack } from './allDayBlockedOverlay';

/** Clear of the cursor, so the card never sits under the pointer itself. */
const CURSOR_OFFSET_PX = 14;

/**
 * Washes that outrank this one where they overlap.
 *
 * A slot can be both inside the minimum-notice window and outside working
 * hours, and the calendar paints both. Minimum notice wins: it is the harder
 * constraint — no interviewer choice makes that time bookable — and two cards
 * under one cursor is worse than either alone.
 */
const MINIMUM_NOTICE_CLASS = 'bg-minimum-notice-disabled-slots';

/**
 * FullCalendar wraps every FOREGROUND event in this harness; a background wash
 * lives in the bg harness instead. So it names exactly what paints ON TOP of
 * the wash — booked interviews, busy and free blocks, meeting rooms — without
 * enumerating each calendar's own event classes.
 */
const EVENT_HARNESS_SELECTOR = '.fc-timegrid-event-harness';

export type UseUnavailableHoursHoverCardArgs = {
  /** The calendar root the cursor moves across — usually one it already has. */
  hostRef: React.RefObject<HTMLElement | null>;
  /** The wash's own class, from the calendar's event-class constants. */
  washClassName: string;
  /** Off when the calendar paints no wash, so the listener never attaches. */
  enabled?: boolean;
  description?: string;
};

/** The card, portalled to `body`, or `null` when the cursor is elsewhere. */
export function useUnavailableHoursHoverCard({
  hostRef,
  washClassName,
  enabled = true,
  description,
}: UseUnavailableHoursHoverCardArgs): React.ReactNode {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Read live inside the listener so a changed class or copy never has to
  // re-attach it mid-hover.
  const washClassRef = useRef(washClassName);
  washClassRef.current = washClassName;

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !enabled) return;

    const onMove = (event: MouseEvent) => {
      // Optional-called: not every DOM implementation ships it (jsdom does
      // not), and this is an enhancement — losing it must never break the
      // calendar underneath.
      const stack =
        document.elementsFromPoint?.(event.clientX, event.clientY) ?? [];
      // Topmost first, stopping at whichever is drawn highest here: an event
      // over the wash owns the cursor. It says its own name, and this card is
      // only ever about the empty grey around it (CORE-5215).
      let overWash = false;
      for (const el of stack) {
        if (el.closest?.(EVENT_HARNESS_SELECTOR)) break;
        if (el.classList?.contains(washClassRef.current)) {
          overWash = true;
          break;
        }
      }
      const outranked =
        stack.some((el) => el.classList?.contains(MINIMUM_NOTICE_CLASS)) ||
        Boolean(pickAllDayTitlesFromStack(stack)?.length);
      if (!overWash || outranked) {
        setPosition((prev) => (prev === null ? prev : null));
        return;
      }
      // Flip to the other side of the cursor near a viewport edge, so the card
      // is never clipped by the drawer it is explaining something inside.
      const left =
        window.innerWidth - event.clientX >
        UNAVAILABLE_HOURS_CARD_WIDTH_PX + CURSOR_OFFSET_PX
          ? event.clientX + CURSOR_OFFSET_PX
          : event.clientX - UNAVAILABLE_HOURS_CARD_WIDTH_PX - CURSOR_OFFSET_PX;
      const top =
        window.innerHeight - event.clientY >
        UNAVAILABLE_HOURS_CARD_HEIGHT_PX + CURSOR_OFFSET_PX
          ? event.clientY + CURSOR_OFFSET_PX
          : event.clientY - UNAVAILABLE_HOURS_CARD_HEIGHT_PX - CURSOR_OFFSET_PX;
      setPosition({ top, left });
    };
    const onLeave = () => setPosition(null);

    host.addEventListener('mousemove', onMove);
    host.addEventListener('mouseleave', onLeave);
    return () => {
      host.removeEventListener('mousemove', onMove);
      host.removeEventListener('mouseleave', onLeave);
    };
  }, [hostRef, enabled]);

  if (!position) return null;
  // Portalled so the `fixed` coordinates resolve against the viewport: the
  // wizard mounts inside a transformed modal wrapper, which would otherwise
  // become the containing block and offset the card from the cursor.
  return createPortal(
    <UnavailableHoursHoverCard
      top={position.top}
      left={position.left}
      description={description}
    />,
    document.body
  );
}
